import { useEffect, useState, useRef } from 'react';
import { Plus, Trash2, Edit2, Upload, Link, Loader2, Image as ImageIcon, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { value: 'exterior', label: 'Exterior' },
  { value: 'main_floor', label: 'Main Floor' },
  { value: 'upper_level', label: 'Upper Level' },
  { value: 'lower_level', label: 'Lower Level' },
  { value: 'amenities', label: 'Floor Plans' },
];

export default function GalleryManager() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [uploadMode, setUploadMode] = useState('upload'); // 'upload' or 'url'
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    url: '',
    alt: '',
    category: 'exterior',
    order: 0,
  });

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const res = await axios.get(`${API}/gallery`);
      setImages(res.data);
    } catch (error) {
      toast.error('Failed to load gallery');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (image = null) => {
    if (image) {
      setEditingImage(image);
      setFormData({
        url: image.url,
        alt: image.alt,
        category: image.category,
        order: image.order || 0,
      });
      setUploadMode('url');
      setPreviewUrl(image.url);
    } else {
      setEditingImage(null);
      setFormData({
        url: '',
        alt: '',
        category: 'exterior',
        order: 0,
      });
      setUploadMode('upload');
      setPreviewUrl('');
      setSelectedFile(null);
    }
    setDialogOpen(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error('File size must be less than 10MB');
        return;
      }
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (uploadMode === 'upload' && !editingImage) {
      // File upload mode
      if (!selectedFile) {
        toast.error('Please select an image file');
        return;
      }
      if (!formData.alt) {
        toast.error('Please enter a description');
        return;
      }

      setSubmitting(true);
      try {
        const uploadData = new FormData();
        uploadData.append('file', selectedFile);
        uploadData.append('alt', formData.alt);
        uploadData.append('category', formData.category);
        uploadData.append('order', formData.order.toString());

        await axios.post(`${API}/gallery/upload`, uploadData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        toast.success('Image uploaded successfully');
        setDialogOpen(false);
        setSelectedFile(null);
        setPreviewUrl('');
        fetchImages();
      } catch (error) {
        toast.error('Failed to upload image');
      } finally {
        setSubmitting(false);
      }
    } else {
      // URL mode or editing
      if (!formData.url || !formData.alt) {
        toast.error('Please fill in all required fields');
        return;
      }

      setSubmitting(true);
      try {
        if (editingImage) {
          await axios.put(`${API}/gallery/${editingImage.id}`, formData);
          toast.success('Image updated successfully');
        } else {
          await axios.post(`${API}/gallery`, formData);
          toast.success('Image added successfully');
        }
        setDialogOpen(false);
        fetchImages();
      } catch (error) {
        toast.error('Failed to save image');
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    
    try {
      await axios.delete(`${API}/gallery/${id}`);
      toast.success('Image deleted');
      fetchImages();
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const getImageUrl = (url) => {
    if (url.startsWith('/api/')) {
      return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    }
    return url;
  };

  const filteredImages = filterCategory === 'all' 
    ? images 
    : images.filter(img => img.category === filterCategory);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-serif text-3xl text-[#1C1C1C] mb-2">Gallery Manager</h1>
          <p className="text-[#66605B]">Upload, edit, and organize property photos</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-[#1B4D3E] hover:bg-[#153E32]"
          data-testid="add-image-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Image
        </Button>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-4">
        <Label>Filter by Category:</Label>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-[#66605B] ml-auto">
          {filteredImages.length} image{filteredImages.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Images Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-[#1B4D3E]" />
        </div>
      ) : filteredImages.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredImages.map((img) => (
            <div
              key={img.id}
              className="bg-white rounded-sm shadow-sm overflow-hidden group"
            >
              <div className="aspect-square relative">
                <img
                  src={getImageUrl(img.url)}
                  alt={img.alt}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/400?text=Image+Not+Found';
                  }}
                />
                {img.is_local && (
                  <div className="absolute top-2 left-2 bg-[#1B4D3E] text-white text-xs px-2 py-1 rounded">
                    Uploaded
                  </div>
                )}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => handleOpenDialog(img)}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDelete(img.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="p-3">
                <p className="text-sm text-[#1C1C1C] truncate">{img.alt}</p>
                <p className="text-xs text-[#66605B] capitalize">{img.category.replace('_', ' ')}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-sm shadow-sm p-12 text-center">
          <ImageIcon className="w-12 h-12 mx-auto mb-4 text-[#66605B] opacity-30" />
          <p className="text-[#66605B]">No images in this category</p>
          <Button
            onClick={() => handleOpenDialog()}
            variant="outline"
            className="mt-4"
          >
            Add First Image
          </Button>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif">
              {editingImage ? 'Edit Image' : 'Add New Image'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {!editingImage && (
              <Tabs value={uploadMode} onValueChange={setUploadMode}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="upload" className="flex items-center gap-2">
                    <Upload className="w-4 h-4" />
                    Upload File
                  </TabsTrigger>
                  <TabsTrigger value="url" className="flex items-center gap-2">
                    <Link className="w-4 h-4" />
                    Image URL
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="upload" className="space-y-4 mt-4">
                  <div 
                    className="border-2 border-dashed border-[#E2E0DC] rounded-lg p-8 text-center cursor-pointer hover:border-[#1B4D3E] transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {selectedFile ? (
                      <div className="space-y-2">
                        <ImageIcon className="w-8 h-8 mx-auto text-[#1B4D3E]" />
                        <p className="text-sm font-medium text-[#1C1C1C]">{selectedFile.name}</p>
                        <p className="text-xs text-[#66605B]">
                          {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedFile(null);
                            setPreviewUrl('');
                          }}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Upload className="w-8 h-8 mx-auto text-[#66605B]" />
                        <p className="text-sm text-[#66605B]">
                          Click to upload or drag and drop
                        </p>
                        <p className="text-xs text-[#66605B]">
                          JPEG, PNG, WebP, GIF (max 10MB)
                        </p>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleFileSelect}
                    />
                  </div>
                </TabsContent>

                <TabsContent value="url" className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="url">Image URL *</Label>
                    <Input
                      id="url"
                      value={formData.url}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, url: e.target.value }));
                        setPreviewUrl(e.target.value);
                      }}
                      placeholder="https://..."
                    />
                  </div>
                </TabsContent>
              </Tabs>
            )}

            {editingImage && (
              <div className="space-y-2">
                <Label htmlFor="url">Image URL *</Label>
                <Input
                  id="url"
                  value={formData.url}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, url: e.target.value }));
                    setPreviewUrl(e.target.value);
                  }}
                  placeholder="https://..."
                  disabled={editingImage?.is_local}
                />
                {editingImage?.is_local && (
                  <p className="text-xs text-[#66605B]">This is an uploaded file. URL cannot be changed.</p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="alt">Description *</Label>
              <Input
                id="alt"
                value={formData.alt}
                onChange={(e) => setFormData(prev => ({ ...prev, alt: e.target.value }))}
                placeholder="Image description"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Category</Label>
              <Select 
                value={formData.category} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="order">Display Order</Label>
              <Input
                id="order"
                type="number"
                value={formData.order}
                onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                min="0"
              />
            </div>

            {previewUrl && (
              <div className="border rounded-sm p-2">
                <p className="text-xs text-[#66605B] mb-2">Preview:</p>
                <img
                  src={previewUrl.startsWith('/api/') ? getImageUrl(previewUrl) : previewUrl}
                  alt="Preview"
                  className="w-full h-40 object-cover rounded"
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-[#1B4D3E] hover:bg-[#153E32]">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingImage ? 'Update' : uploadMode === 'upload' ? 'Upload' : 'Add'} Image
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
